import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from 'react-native-paper';
import { useClient } from '../providers/ClientProvider';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParams } from './Home';
import { useNav } from '../providers/NavProvider';
import { getObjectByPath } from '../services/Utils';
import { ISitee } from './ClientLanding';
import { useFocusEffect } from '@react-navigation/native';
import Header from './Header';
import Animated, { useSharedValue, useAnimatedStyle, withSpring } from 'react-native-reanimated';
import { Pressable, ScrollView } from 'react-native';
import LogProvider from './Logs/LogProvider';

type Props = NativeStackScreenProps<RootStackParams, "Portfolio">

const Portfolio: React.FC<Props> = ({ route }) => {
    const { setCurrentClientShortURL, clientObj } = useClient();
    //const navigation = useNavigation<NativeStackNavigationProp<RootStackParams>>();
    const { sitesResp, setCurrentLeafObj, currentLeafObj } = useNav();
    const { clientName } = useClient();
    const [siteObj, setSiteObj] = React.useState<ISitee>(null);
    const [projectId, setProjectId] = useState(null);
    const [uniqueLists, setUniqueLists] = useState<ISitee[]>([]);
    const [selectedList, setSelectedList] = useState<ISitee | null>(null);

    // Animation values for the pill
    const pillX = useSharedValue(0);
    const pillWidth = useSharedValue(0);

    const animatedPillStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: pillX.value }],
        width: pillWidth.value,
    }));

    // Recursive function to find all unique lists
    const findAllLists = (node: ISitee, collected: Map<string, ISitee>) => {
        if (node.type === 'list' && !collected.has(node.title)) {
            collected.set(node.title, node);
        }
        if (node.children) {
            node.children.forEach(child => findAllLists(child, collected));
        }
    };

    useFocusEffect(() => {
        if (sitesResp) {
            const listMap = new Map<string, ISitee>();
            findAllLists(sitesResp, listMap);
            const lists = Array.from(listMap.values());
            setUniqueLists(lists);
            if (lists.length > 0 && !selectedList) {
                setSelectedList(lists[0]);
            }
        }

        if (siteObj) {
            setCurrentLeafObj(siteObj);
            if (sitesResp) {
                const projectSite = findProjectParent(sitesResp, siteObj.id);
                if (projectSite && projectSite.path.split('-').length === 2) {
                    siteObj.id && setProjectId(projectSite.id);
                }
            }
        }
    });
    function findProjectParent(siteResp: any, siteId: string): any | null {
        function findParentById(site: any, id: string): any | null {
            if (!site.children) return null;

            for (const child of site.children) {
                if (child.id === id) return site; // Direct parent found
                const parent = findParentById(child, id);
                if (parent) return parent; // Recursively check parents
            }
            return null;
        }

        // Start from given site and move up until we reach project-level site
        let currentSite = siteResp;
        let parent = findParentById(siteResp, siteId);

        while (parent) {
            // If the parent is a project-level site (2 segments in path), return it
            if (parent.path.split("-").length === 2) {
                return parent;
            }
            // Move up the hierarchy
            currentSite = parent;
            parent = findParentById(siteResp, currentSite.id);
        }
        return null; // No project-level parent found
    }

    React.useEffect(() => {
        setCurrentClientShortURL(route.params.clientShortURL);
        if (sitesResp) {
            const tempsiteObj = getObjectByPath(decodeURIComponent(route.params.sitePath), sitesResp);
            // setCurrentLeafObj(tempsiteObj);
            setSiteObj(tempsiteObj);
        }
    }, [route && route.params && route.params.sitePath && route.params.sitePath, sitesResp]);
    const handleTabPress = (list: ISitee, index: number, layout: { x: number, width: number }) => {
        setSelectedList(list);
        pillX.value = withSpring(layout.x);
        pillWidth.value = withSpring(layout.width);
    };

    return (
        <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
            <Header />

            <View style={styles.navWrapper}>
                {(siteObj && siteObj.title) &&
                    <View style={styles.headerBanner}>
                        <Text variant='titleLarge' style={{ color: '#fff' }} >{siteObj.title} Portfolio</Text>
                    </View>
                }

                {/* Dynamic Pill Navigation */}
                <View style={styles.tabBarContainer}>
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.tabsScrollContent}
                    >
                        {/* Animated Pill Background */}
                        <Animated.View style={[styles.pill, animatedPillStyle]} />

                        {uniqueLists.map((list, index) => (
                            <Pressable
                                key={list.id}
                                onLayout={(event) => {
                                    // Set initial position if this is the first item
                                    if (selectedList?.title === list.title && pillWidth.value === 0) {
                                        const { x, width } = event.nativeEvent.layout;
                                        pillX.value = x;
                                        pillWidth.value = width;
                                    }
                                }}
                                onPress={(event) => {
                                    // Type-safety for target
                                    const target = event.currentTarget as any;
                                    target.measure((x: number, y: number, width: number, height: number, pageX: number) => {
                                        // We need relative X within the scrollview, but measure gives screen coords
                                        // Simplest is to use nativeEvent.layout but it's only onLayout.
                                        // For now using a simpler approach or pre-calculating layouts.
                                    });
                                    // Alternative: use onLayout to cache positions
                                }}
                                // Re-using onLayout for simpler implementation since we need relative coords
                                onTouchStart={() => { }} // dummy to enable touch
                                onPressIn={(e) => {
                                    // Better way: use the layout from onLayout
                                }}
                            >
                                <TabButton
                                    list={list}
                                    isSelected={selectedList?.title === list.title}
                                    onPress={(layout) => handleTabPress(list, index, layout)}
                                />
                            </Pressable>
                        ))}
                    </ScrollView>
                </View>

                {/* Content Area */}
                <View style={styles.contentContainer}>
                    {selectedList ? (
                        <View style={{ flex: 1 }}>
                            <LogProvider
                                logTitle={selectedList.title}
                                parentId={selectedList.parentId}
                                Permissions={JSON.stringify(selectedList.permissions || []).toLowerCase()}
                                listObj={selectedList}
                                sitesResp={sitesResp}
                            />
                        </View>
                    ) : (
                        <View style={styles.emptyState}>
                            <Text variant='bodyMedium'>No lists found in this portfolio.</Text>
                        </View>
                    )}
                </View>
            </View>
        </View>
    )
}

const TabButton = ({ list, isSelected, onPress }: { list: ISitee, isSelected: boolean, onPress: (layout: { x: number, width: number }) => void }) => {
    const [layout, setLayout] = useState({ x: 0, width: 0 });
    return (
        <View
            onLayout={(e) => setLayout(e.nativeEvent.layout)}
            style={styles.tabButtonItem}
        >
            <Pressable onPress={() => onPress(layout)}>
                <Text
                    style={[
                        styles.tabText,
                        isSelected ? styles.tabTextActive : styles.tabTextInactive
                    ]}
                >
                    {list.title}
                </Text>
            </Pressable>
        </View>
    );
};

const styles = StyleSheet.create({
    navWrapper: {
        flex: 1,
    },
    headerBanner: {
        backgroundColor: '#102B27',
        width: "100%",
        padding: 15,
        alignItems: 'center',
        elevation: 4,
    },
    tabBarContainer: {
        backgroundColor: '#fff',
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
    },
    tabsScrollContent: {
        paddingHorizontal: 15,
        alignItems: 'center',
    },
    pill: {
        position: 'absolute',
        height: 36,
        backgroundColor: '#4BB75F',
        borderRadius: 18,
        bottom: 0,
        // The animated values width and transform.translateX are applied via useAnimatedStyle
    },
    tabButtonItem: {
        paddingHorizontal: 20,
        paddingVertical: 8,
        zIndex: 2,
    },
    tabText: {
        fontSize: 14,
        fontWeight: '600',
    },
    tabTextActive: {
        color: '#fff',
    },
    tabTextInactive: {
        color: '#666',
    },
    contentContainer: {
        flex: 1,
        padding: 10,
    },
    emptyState: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    }
});

export default Portfolio;